import { ChangeType } from "../interfaces/DTO/agent/mimshakGroupBy";
import { Contact } from "../interfaces/DTO/crm/contact";
import { Product } from "../interfaces/DTO/mislaka_xml_models";
import { User } from "../interfaces/DTO/user";
import { HandlerEntry } from "../services/fundService";
import { LOCAL_STORAGE_KEYS } from "../services/local_storage_keys";
import { DateTime } from "luxon";

export const formatName = (user: User|Contact): string => {
    if (user==null) return '';
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName  || ''; // אם אין שם פרטי ומשפחה, הצג את שם המשתמש
  };

  export const formatFullName = (product: Product): string => {
    if (!product || !product.client) return '';
    
    const firstName = product.client.client_name || "";
    const lastName = product.client.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || "לקוח ללא שם"; // אם אין שם פרטי ומשפחה, הצג ברירת מחדל
};
export const formatNumberFloor = (value: number): string => {
  if (isNaN(value) || value === null || value === undefined) {
    return "";
  }
  return new Intl.NumberFormat("he-IL", {
    maximumFractionDigits: 0, // ללא ספרות אחרי הנקודה
  }).format(value);
};
export const formatAge = (dateOfBirth: string): string => {
  if (!dateOfBirth || isNaN(Date.parse(dateOfBirth))) {
    return "";
  }

  const birthDate = new Date(dateOfBirth);
  const today = new Date();

  const ageInMilliseconds = today.getTime() - birthDate.getTime();
  const ageInYears = ageInMilliseconds / (1000 * 60 * 60 * 24 * 365.25);

  return `${ageInYears.toFixed(1)}`;
};
export const calculateAgeInYear = (dateOfBirth: string, year: number): string => {
  if (!dateOfBirth || isNaN(Date.parse(dateOfBirth)) || isNaN(year)) {
    return "";
  }

  const birthDate = new Date(dateOfBirth);
  const targetDate = new Date(year, birthDate.getMonth(), birthDate.getDate());

  let age = targetDate.getFullYear() - birthDate.getFullYear();

  // אם תאריך הלידה עדיין לא עבר באותה שנה, נפחית שנה אחת
  if (
    targetDate.getMonth() < birthDate.getMonth() ||
    (targetDate.getMonth() === birthDate.getMonth() && targetDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return `${age}`;
};
export const formatDateCategoryWithAge = (
  liquidityDateCategory: string,
  dateOfBirthList: (string | null | undefined)[]
): { label: string; ageText?: string } => {
  let label = "";
  switch (liquidityDateCategory) {
    case "ALREADY_LIQUID":
      label = "כבר נזיל";
      break;
    case "LIQUID_THIS_YEAR":
      label = "נזיל בהמשך השנה";
      break;
    case "UNKNOWN":
      label = "תאריך לא ידוע";
      break;
    case "PENSION_AT_67":
      label = "קצבה לגיל 67";
      break;
    case "RETIREMENT_AND_ABOVE":
      label = "נזיל מגיל 67+";
      break;
    default:
      label = liquidityDateCategory;
  }

  if (!isNaN(Number(liquidityDateCategory))) {
    const ages = dateOfBirthList
      .filter((dob) => !!dob)
      .map((dob) => calculateAgeInYear(dob!, Number(liquidityDateCategory)))
      .join(", ");

    return { label, ageText: ages ? `גיל: ${ages}` : undefined };
  }

  return { label };
};

export const formatCurrency = (value: number): string => {
  if (isNaN(value) || value === null || value === undefined) {
    return "";
  }
  return `${Math.floor(value).toLocaleString()} ₪`;
}
export const formatCurrencyCompact = (
  value: number,
  compact: boolean = true
): string => {
  if (isNaN(value) || value === null || value === undefined) {
    return "";
  }

  if (!compact) {
    return formatCurrency(value); // קריאה לפורמט הרגיל אם compact הוא false
  }

  if (value < 10000) {
    return `${Math.floor(value)} ₪`; // אין צורך להציג אחרי הנקודה
  }

  const suffixes = ["", "K", "M"];
  let index = 0;

  while (value >= 1000 && index < suffixes.length - 1) {
    value /= 1000;
    index++;
  }

  const formattedValue = value < 10 ? value.toFixed(2) : value.toFixed(1); // תצוגת 4 ספרות
  return `${formattedValue}${suffixes[index]} ₪`;
};
export const formatPercentage = (value: number): string => {
  if (isNaN(value) || value === null || value === undefined) {
    return "";
  }
  return `${value.toFixed(2)}%`;
}
export const formatPercentageRound = (value: number): string => {
  if (isNaN(value) || value === null || value === undefined) {
    return "";
  }
  return `${value.toFixed(0)}%`;
}

export const formatNumber = (value: number): string => {
  if (isNaN(value) || value === null || value === undefined) {
    return "";
  }
  return `${value.toFixed(2)}`;
}
export const formatIsoDateToMMYYYY = (isoDate: string): string => {  
  const date = new Date(isoDate);
  return `${date.getMonth() + 1}-${date.getFullYear()}`;
}

export const formatIsoDate = (isoDate: string): string => {
  if (!isoDate) return ""; // טיפול במקרה של תאריך ריק
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return ""; // תאריך לא תקין – לא להציג NaN-NaN-NaN

  // הוספת אפסים מובילים ליום ולחודש (למשל: 01-09-2024 במקום 1-9-2024)
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

/**
 * The server keeps UTC without a zone marker - read it as UTC (a string that has a zone is read as is).
 * parseUtcIsoDate("2026-10-19T11:30:00") → Date of 19/10/2026 14:30 Israel time
 * parseUtcIsoDate("2026-10-19T11:30:00+03:00") → Date of 19/10/2026 11:30 Israel time
 */
export const parseUtcIsoDate = (iso: string): Date =>
  new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

/**
 * A date only (no time) as day.month, without the year.
 * formatIsoDateDayMonth("2026-10-31") → "31.10"
 */
export const formatIsoDateDayMonth = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit" });

// מציג תאריך נזילות ביחס לתאריך ייחוס: "נזיל" אם עבר, "נזיל בעוד X חודשים"/"נזיל בעוד X שנים" אם עתידי, "לא נזיל" אם אין תאריך או שהוא רחוק מדי (מעל 80 שנה)
export const formatIsoDateYearly = (liquidityDate: string | null | undefined, fromDate: string | Date): string => {
  if (!liquidityDate) return "לא נזיל";

  const from = new Date(fromDate);
  const target = new Date(liquidityDate);

  if (target <= from) return "נזיל";

  const totalMonths =
    (target.getFullYear() - from.getFullYear()) * 12 + (target.getMonth() - from.getMonth());

  if (totalMonths / 12 > 80) return "לא נזיל";

  if (totalMonths < 12) {
    return `נזיל בעוד ${totalMonths} חודשים`;
  }

  const years = Math.round(totalMonths / 12);
  return `נזיל בעוד ${years} שנים`;
};
export const formatIsoDateWithTime = (isoDate: string): string => {
  // ודא שהקלט מפורש כ־UTC גם אם חסר Z
  const utcDate = isoDate.endsWith("Z") || isoDate.includes("+")
    ? DateTime.fromISO(isoDate, { zone: "utc" })
    : DateTime.fromISO(isoDate + "Z", { zone: "utc" });

  const israelTime = utcDate.setZone("Asia/Jerusalem");

  const now = DateTime.now().setZone("Asia/Jerusalem").startOf("day");
  const dateOnly = israelTime.startOf("day");
  const diffDays = dateOnly.diff(now, "days").days;

  const timePart = israelTime.toFormat("HH:mm");

  if (diffDays === 0) return `היום ${timePart}`;
  if (diffDays === -1) return `אתמול ${timePart}`;
  if (diffDays === 1) return `מחר ${timePart}`;

  // fallback רגיל לתאריך מלא אם זה לא היום/אתמול/מחר
  return israelTime.toFormat("dd/MM/yyyy HH:mm");
};

/**
 * כמו formatIsoDateWithTime אבל לתאריכים ישנים מציג רק תאריך (ללא שעה).
 * היום/אתמול/מחר — עם שעה. לפני כן — dd/MM/yyyy בלבד.
 */
export const formatIsoDateTimeDays = (isoDate: string): string => {
  const utcDate = isoDate.endsWith("Z") || isoDate.includes("+")
    ? DateTime.fromISO(isoDate, { zone: "utc" })
    : DateTime.fromISO(isoDate + "Z", { zone: "utc" });

  const israelTime = utcDate.setZone("Asia/Jerusalem");

  const now = DateTime.now().setZone("Asia/Jerusalem").startOf("day");
  const dateOnly = israelTime.startOf("day");
  const diffDays = dateOnly.diff(now, "days").days;

  const timePart = israelTime.toFormat("HH:mm");

  if (diffDays === 0) return `היום ${timePart}`;
  if (diffDays === -1) return `אתמול ${timePart}`;
  if (diffDays === 1) return `מחר ${timePart}`;
  if (diffDays === -2) return `לפני יומיים`;
  if (diffDays === 2) return `בעוד יומיים`;

  const absDays = Math.round(Math.abs(diffDays));
  return diffDays < 0 ? `לפני ${absDays} ימים` : `בעוד ${absDays} ימים`;
};

export const formatReportPeriod = (period: string): string => {
  if (period.length !== 6) {
    throw new Error("Invalid format. Expected 'YYYYMM'.");
  }

  const year = period.slice(0, 4); // ארבע הספרות הראשונות הן השנה
  const month = period.slice(4, 6); // שתי הספרות האחרונות הן החודש

  return `${month}-${year}`; // חיבור בפורמט MM-YYYY
};
/**
 * מחשבת את שם המשפחה הדיפולטי בהתבסס על רשימת Contributors.
 * @param contributors - רשימת משתמשים (contributors)
 * @returns שם המשפחה הדיפולטי כטקסט.
 */
export const computeDefaultFamilyName = (contributors: User[]): string => {
  const contributorsLastNames = contributors.map(
    (contributor) => contributor.last_name || ""
  );
  const isLastNameIdentical =
    contributorsLastNames.length > 1 &&
    contributorsLastNames[0] === contributorsLastNames[1];

  return isLastNameIdentical
    ? `משפחת ${contributorsLastNames[0]}`
    : `משפחת ${contributorsLastNames.join(" ")}`;
};

export const formatUniversalPhone = (phone: string): string => {
  if (!phone) return "";

  // 🔹 הסרת תווים שאינם מספרים
  let cleanedPhone = phone.replace(/\D/g, "");

  // 🔹 אם המספר מתחיל ב-0, להחליף אותו ב-972
  if (cleanedPhone.startsWith("0")) {
    cleanedPhone = "972" + cleanedPhone.slice(1);
  }

  // 🔹 אם כבר מתחיל ב-972, להשאיר אותו כמו שהוא
  if (!cleanedPhone.startsWith("972")) {
    cleanedPhone = "972" + cleanedPhone;
  }

  return cleanedPhone;
};
export const formatChangeType = (changeType: ChangeType): string => {
  switch (changeType) {
    case ChangeType.NEW_POLICY:
      return "פוליסה חדשה";
    case ChangeType.DEPOSIT_FEE_CHANGED:
      return "שינוי בדמי ניהול הפקדה";
    case ChangeType.SAVING_FEE_CHANGED:
      return "שינוי בדמי ניהול צבירה";
    case ChangeType.DEPOSIT_AMOUNT_DECREASED:
      return "ירידה בסכום הפקדות";
    case ChangeType.WITHDRAWAL_AMOUNT_INCREASED:
      return "עלייה בסכום משיכות";
    case ChangeType.POLICY_BALANCE_ZEROED:
      return "איפוס יתרה";
    case ChangeType.TRANSFER_OUT_CHANGED:
      return "כספים יוצאים"; // ✅ טקסט חדש לשינוי בכספים יוצאים
    case ChangeType.TRANSFER_IN_CHANGED:
      return "כספים נכנסים"; // ✅ טקסט חדש לשינוי בכספים נכנסים
    case ChangeType.AGENT_APPOINTMENT_DATE_CHANGED:
      return 'מינוי סוכן חדש'; // ✅ טקסט חדש
    case ChangeType.FUND_IDS_CHANGED:
      return 'שינוי מסלול'; // ✅ טקסט חדש
    case ChangeType.MISSING_POLICY:
      return 'פוליסה חסרה בתקופה הנוכחית'; // ✅ טקסט חדש
    default:
      return "לא ידוע";
  }
};
export const formatHandlerCode2 = (HANDLER_CODE: string): string => {
  const handlerMap: { [key: string]: string } = {
    "511880460": "אנליסט",
    "513611509": "ילין לפידות",
    "513173393": "אלטשולר",
    "520023185": "פניקס",
    "513026484": "פניקס",
    "512237744": "מגדל",
    "512267592": "הראל",
    "520004078": "הראל ביטוח",
    "512065202": "מיטב", 
    "512245812": "מנורה",
    "513621110": "אינפיניטי",
    "514956465": "מור",
    "520042177": "הכשרה ביטוח",
    "512244146": "כלל",
    "520024647": "כלל ביטוח",
    "520042540": "מנורה מבטחים",
    "520004896": "מגדל ביטוח",
    "512304882": "AIG",
  };

  return handlerMap[HANDLER_CODE] || HANDLER_CODE;
};
let cachedHandlerList: HandlerEntry[] | null = null;

export const getHandlerCodeList = (): HandlerEntry[] => {
  if (!cachedHandlerList) {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.handlerCodeMap);
      cachedHandlerList = raw ? JSON.parse(raw) : [];
    } catch {
      cachedHandlerList = [];
    }
  }
  return cachedHandlerList;
};

export const formatHandlerCode = (handlerCode: string): string => {
  const handlerList = getHandlerCodeList();
  const entry = handlerList.find((h) => h.code === handlerCode);

  // אם איבר אין ברשימה מה-localStorage, בדוק בטבלת המיפוי הידנית
  if (!entry) {
    const manualMapping: { [key: string]: string } = {
      "512304882": "איי אי ג'י",
      "520040122": "איי אי ג'י", // AIG
      "520030677": "איילון ביטוח", // 520042169 חוסל עקב מיזוג
      "513910703": "איי די איי", // I.D.I
      "515761625": "ליברה", // ליברה חברה לביטוח
      "510015951": "שומרה", // שומרה חברה לביטוח
      "520020504": "קרן הגמלאות המרכזית של עובדי ההסתדרות",
    };

    if (manualMapping[handlerCode]) {
      return manualMapping[handlerCode];
    }
  }

  return entry?.name || handlerCode;
};
export const resetHandlerCodeMapCache = () => {
  cachedHandlerList = null;
};

export function getContactAddress(contact: Contact): string {
  const { city, street, house_number } = contact;

  // מסנן שדות ריקים או undefined ומחבר עם פסיקים
  return [city, street, house_number].filter(Boolean).join(", ");
}

/**
 * פונקציה לפורמט משך זמן בדקות לפורמט קריא
 * @param durationMinutes - משך זמן בדקות
 * @returns מחרוזת מפורמטת של משך הזמן
 */
export const formatDuration = (durationMinutes: number | null | undefined): string => {
  if (!durationMinutes) return "לא ידוע";
  const roundedDuration = Math.round(durationMinutes);
  const hours = Math.floor(roundedDuration / 60);
  const minutes = roundedDuration % 60;
  if (hours > 0) {
    return `${hours} שעות ${minutes} דקות`;
  }
  return `${minutes} דקות`;
};

export const formatCurrentTimeGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "בוקר טוב";
  if (hour >= 12 && hour < 17) return "צהריים טובים";
  return "ערב טוב";
};